import React, { useEffect, useState, useRef } from 'react';
import { Button, Form, Input, Table, Select, Space, Modal, DatePicker, Tabs, Tooltip } from "antd";
import { getGridTableHeight, isEmpty, getNowAddMonthDate, dateFormatter, dataSingleNewlineProc,dataOrgNewlineProc } from '../Common/util';
import { EditableCell, EditableRow } from '../Common/function';
import SelectBox from '../Common/SelectBox';
import { applyActionSelectOptions, applyTimeSelectOptions } from '../Common/options';
import { SearchOutlined, InfoCircleOutlined } from '@ant-design/icons';
import Highlighter from 'react-highlight-words';
import dayjs from 'dayjs';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const { Option } = Select;

/** --------------------------- ----------------------------------------------- **/
const PlanTableRs = ({ data, columnData, onClickSaveHandler, rowSelection }) => {

    /** --------------------------- ----------------------------------------------- **/
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef(null);

    // 모달 관련 state
    const [isRemarkModalVisible, setIsRemarkModalVisible] = useState(false);
    const [currentRemark, setCurrentRemark] = useState('');
    const [currentRecord, setCurrentRecord] = useState(null);
    const [activeTab, setActiveTab] = useState('edit');

    // 미조치 사유 모달 열기
    const showRemarkModal = (record) => {
        setCurrentRecord(record);
        setCurrentRemark(record.remark || '');
        setActiveTab('edit');
        setIsRemarkModalVisible(true);
    };

    // 미조치 사유 모달 저장
    const handleRemarkOk = () => {
        if (currentRecord) {
            const newData = [...dataSource];
            const index = newData.findIndex((item) =>
                (currentRecord.asset_cd == item.asset_cd) &&
                (currentRecord.measure_cd == item.measure_cd)
            );
            if (index > -1) {
                newData[index] = {
                    ...newData[index],
                    remark: currentRemark,
                    operation: 'U'
                };
                setDataSource(newData);
            }
        }
        setIsRemarkModalVisible(false);
        setCurrentRecord(null);
        setCurrentRemark('');
    };

    // 미조치 사유 모달 취소
    const handleRemarkCancel = () => {
        setIsRemarkModalVisible(false);
        setCurrentRecord(null);
        setCurrentRemark('');
    };
    const handleSearch = (selectedKeys, confirm, dataIndex) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };
    const handleReset = (clearFilters, confirm, dataIndex) => {
        clearFilters();
        setSearchText('');
        setSearchedColumn(dataIndex);
        confirm();
    };
    const getColumnSearchProps = (dataIndex) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div
                style={{
                    padding: 8,
                }}
                onKeyDown={(e) => e.stopPropagation()}
            >
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
                    style={{
                        marginBottom: 8,
                        display: 'block',
                    }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{
                            width: 90,
                        }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters, confirm, dataIndex)}
                        size="small"
                        style={{
                            width: 90,
                        }}
                    >
                        Reset
                    </Button>
                    {/* <Button
          type="link"
          size="small"
          onClick={() => {
            confirm({
              closeDropdown: false,
            });
            setSearchText(selectedKeys[0]);
            setSearchedColumn(dataIndex);
          }}
        >
          Filter
        </Button>
        <Button
          type="link"
          size="small"
          onClick={() => {
            close();
          }}
        >
          close
        </Button> */}
                </Space>
            </div>
        ),
        filterIcon: (filtered) => (
            <SearchOutlined
                style={{
                    color: filtered ? '#1677ff' : undefined,
                }}
            />
        ),
        onFilter: (value, record) =>
            record[dataIndex]?.toString().toLowerCase().includes(value.toLowerCase()),
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{
                        backgroundColor: '#ffc069',
                        padding: 0,
                    }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });

    const composeRenders = (...funcs) => (value, record, index) => {
      return funcs.reduceRight((composed, f) => {
        const result = f(value, record, index);
        return result !== undefined ? result : composed;
      }, undefined);
    };

    const getStatusClass = (status) => {
        switch (status) {
            case 'Y':
                return 'yesno-active';
            case 'N':
                return 'yesno-inactive';
            case 'P':
                return 'yesno-pending';
            default:
                return '';
        }
    };

    // 위험관리 계획
    const planColumnData = [
        {
            title: '자산코드',
            dataIndex: 'asset_cd',
            key: 'asset_cd',
            width: '80px',
            align: 'center',
            fixed: 'left',
            ...getColumnSearchProps('asset_cd'),
        },
        {
            title: '진단코드',
            dataIndex: 'measure_cd',
            key: 'measure_cd',
            width: '90px',
            align: 'center',
            fixed: 'left',
            ...getColumnSearchProps('asset_cd'),
        },
        {
            title: '자산명',
            dataIndex: 'asset_nm',
            key: 'asset_nm',
            width: '100px',
            fixed: 'left',
            align: 'center',
            ...getColumnSearchProps('asset_nm'),
        },
        {
            title: 'IP',
            dataIndex: 'asset_ip',
            key: 'asset_ip',
            width: '110px',
            align: 'center',
            sorter: {
                compare: (a, b) => a.asset_ip.localeCompare(b.asset_ip),
            },
            ...getColumnSearchProps('asset_ip'),
        },
        {
            title: '자산유형',
            dataIndex: 'asset_spec',
            key: 'asset_spec',
            width: '100px',
            align: 'center',
            sorter: {
                compare: (a, b) => a.asset_spec.localeCompare(b.asset_spec),
            },
        },
        // {
        //     title: '위험도',
        //     dataIndex: 'risk_score',
        //     key: 'risk_score',
        //     width: '70px',
        //     align: 'center',
        //     sorter: {
        //         compare: (a, b) => a.risk_score - b.risk_score,
        //     },
        // },
        {
            title: '취약점',
            dataIndex: 'measure_nm',
            key: 'measure_nm',
            width: '200px',
            align: 'left',
            ellipsis: false,
            render: (_, rowData) => {
                return (
                    <div style={{ whiteSpace: 'pre-wrap' }}>
                        {rowData.measure_nm}
                    </div>
                );
            },
            sorter: {
                compare: (a, b) => a.measure_nm.localeCompare(b.measure_nm),
            },
            ...getColumnSearchProps('measure_nm'),
        },
        // {
        //     title: '이행 현황',
        //     dataIndex: 'result_desc',
        //     key: 'result_desc',
        //     width: '300px',
        //     align: 'left',
        //     ellipsis : false,        
        //     render: (_ , rowData ) => {
        //         return (
        //             <div style={{ whiteSpace: 'pre-wrap' }}>
        //             {rowData.result_desc}
        //             </div>
        //         );
        //     },
        // },
        //     {
        //         title: '대응전략',
        //         dataIndex: 'apply_action',
        //         key: 'apply_action',
        //         width: '100px',
        //         align: 'center',
        //         className: 'edit_col',
        //         editable: false,
        //         ...getColumnSearchProps('apply_action'),
        //     },
        //     {
        //         title: '적용시기',
        //         dataIndex: 'apply_time',
        //         key: 'apply_time',
        //         width: '100px',
        //         align: 'center',
        //         className: 'edit_col',
        //         editable: false,
        //         ...getColumnSearchProps('apply_time'),
        //     },
        //     {
        //         title: '조치일정',
        //         dataIndex: 'action_time',
        //         key: 'action_time',
        //         width: '100px',
        //         align: 'center',
        //         className: 'edit_col',
        //         editable: false,
        //         ...getColumnSearchProps('action_time'),
        //     },
        //     {
        //         title: '담당자',
        //         dataIndex: 'action_manager',
        //         key: 'action_manager',
        //         width: '100px',
        //         align: 'center',
        //         className: 'edit_col',
        //         editable: false,
        //         ...getColumnSearchProps('action_manager'),
        //     },
        //     {
        //       title: '(장기/위험수용)사유',
        //       dataIndex: 'action_remark',
        //       key: 'action_remark',
        //       width: '180px',
        //       align: 'center',
        //       className: 'edit_col',
        //       editable: false,
        //       ...getColumnSearchProps('action_remark'),
        //   },
        {
            title: '최초진단',
            dataIndex: 'result_init_cd',
            key: 'result_init_cd',
            width: '80px',
            align: 'center',
            sorter: {
                compare: (a, b) => a.result_cd.localeCompare(b.result_cd),
            },
        },
        {
            title: '이행진단',
            dataIndex: 'result_cd',
            key: 'result_cd',
            width: '80px',
            align: 'center',
            className: "yesno-td",
            render: (status) => (
                <div style={{display:"flex",justifyContent:"center", alignItems:"center",widht:"100%",height:"100%",margin:"0",padding:'0'}} 
                className={getStatusClass(status)}>{status}</div>
            ),
            sorter: {
                compare: (a, b) => a.result_cd.localeCompare(b.result_cd),
            },
        },
        {
            title: '이행점검 현황',
            dataIndex: 'result_desc',
            key: 'result_desc',
            width: '150px',
            align: 'center',
            ellipsis: true,
            editable: false,
            ...getColumnSearchProps('result_desc'),
            render: (value, record) => {
                const cleanText = value ? value.replace(/[*#`\-]/g, '').trim() : '';
                const displayText = cleanText ? (cleanText.length > 15 ? cleanText.substring(0, 15) + '...' : cleanText) : '';
                return (
                    <div
                        style={{
                            cursor: value ? 'pointer' : 'default',
                            color: value ? '#1890ff' : 'inherit',
                            textAlign: 'center',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}
                        onClick={() => value && showRemarkModal(record)}
                    >
                        <Tooltip title={value ? "클릭하여 전체 내용 보기" : ""}>
                            <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                                {displayText}
                                {value && <InfoCircleOutlined style={{ marginLeft: 4, fontSize: '12px' }} />}
                            </span>
                        </Tooltip>
                    </div>
                );
            },
        },
        {
            title: '변경여부',
            dataIndex: 'changed',
            key: 'changed',
            width: '90px',
            align: 'center',
            editable: false,
            ...getColumnSearchProps('changed'),
        },
        {
            title: '미조치 사유',
            dataIndex: 'remark',
            key: 'remark',
            width: '200px',
            align: 'left',
            editable: false,
            ...getColumnSearchProps('remark'),
            onCell: (record) => ({
                style: {
                    verticalAlign: 'middle',
                    cursor: 'pointer',
                    padding: 0
                },
                onClick: () => showRemarkModal(record)
            }),
            render: (value, record, index) => {
                return (
                    <div
                        style={{
                            width: '100%',
                            height: '100%',
                            padding: '8px',
                            minHeight: '40px',
                            display: 'flex',
                            alignItems: 'center'
                        }}
                        title="클릭하여 수정"
                    >
                        <div style={{ width: '100%', pointerEvents: 'none' }}>
                            {value ? (
                                <ReactMarkdown
                                    remarkPlugins={[remarkGfm]}
                                    components={{
                                        p: ({node, ...props}) => <p style={{margin: 0, marginBottom: '4px', lineHeight: '1.5'}} {...props} />,
                                        ul: ({node, ...props}) => <ul style={{margin: 0, marginBottom: '4px', paddingLeft: '20px'}} {...props} />,
                                        ol: ({node, ...props}) => <ol style={{margin: 0, marginBottom: '4px', paddingLeft: '20px'}} {...props} />,
                                        h1: ({node, ...props}) => <h1 style={{margin: 0, marginBottom: '4px', fontSize: '1.5em', fontWeight: 'bold'}} {...props} />,
                                        h2: ({node, ...props}) => <h2 style={{margin: 0, marginBottom: '4px', fontSize: '1.3em', fontWeight: 'bold'}} {...props} />,
                                        h3: ({node, ...props}) => <h3 style={{margin: 0, marginBottom: '4px', fontSize: '1.1em', fontWeight: 'bold'}} {...props} />,
                                        h4: ({node, ...props}) => <h4 style={{margin: 0, marginBottom: '4px', fontSize: '1em', fontWeight: 'bold'}} {...props} />,
                                    }}
                                >
                                    {value}
                                </ReactMarkdown>
                            ) : (
                                <span style={{color: '#999'}}>-</span>
                            )}
                        </div>
                    </div>
                );
            },
        },
    ];


    //----------------------------------------------------------------------------------------
    // 변수 선언
    const [form] = Form.useForm();
    const [dataSource, setDataSource] = useState(data);

    // 위험관리계획 일괄수정을 위한 수정값
    const [applyAction, setApplyAction] = useState('');
    const [applyTime, setApplyTime] = useState('');
    const [actionTime, setActionTime] = useState('');
    const [actionManager, setActionManager] = useState('');

    let changeData = [];

    useEffect(() => {
        setDataSource(data);
    }, [data]);

    const operationColumns = [
        {
            key: 'operation',
            width: '0px',
            defualt: '',
        },
    ];

    // 미확정상태의 경우 operation칼럼 추가 및 edit기능 추가
    let mergedColumns = [];
    ///const columns = [...columnData];
    const columns = [...planColumnData];
    operationColumns.map((col) => {
        columns.push(col);
    })
    mergedColumns = columns.map((col) => {
        if (!col.editable) { return col; }
        return {
            ...col,
            onCell: (record) => ({
                record,
                editable: col.editable,
                dataIndex: col.dataIndex,
                title: col.title,
                handleSave,
            }),
        };
    });

    //행 저장
    const handleSave = (row) => {
        const newData = [...dataSource];
        const index = newData.findIndex((item) => ((row.asset_cd == item.asset_cd) && (row.measure_cd == item.measure_cd)));
        const item = newData[index];

        if (JSON.stringify(item) != JSON.stringify(row)) {
            row.operation = row.id == '0' ? 'C' : 'U';
            newData.splice(index, 1, {
                ...item,
                ...row,
            });
            setDataSource(newData);
        }
    };

    useEffect(() => {
        //저장,수정 대상 추가
        const newData = dataSource.filter((item) => item.operation && item.operation != '');
        changeData = [];
        newData.map((record) => {
            if (record.operation && record.operation != '') {
                changeData = changeData.concat(record);
            }
        })

    }, [dataSource]);

    //----------------------------------------------------------------------------------------
    return (
        <>
            <Modal
                title="사유 수정"
                open={isRemarkModalVisible}
                onOk={handleRemarkOk}
                onCancel={handleRemarkCancel}
                okText="저장"
                cancelText="취소"
                width={1200}
            >
                <div style={{ display: 'flex', gap: '16px' }}>
                    {/* 왼쪽: 이행점검 현황 */}
                    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                        <h4 style={{ marginTop: 0, marginBottom: '12px', flexShrink: 0 }}>이행점검 현황</h4>
                        {currentRecord && (
                            <div style={{
                                height: '600px',
                                padding: '12px',
                                border: '1px solid #d9d9d9',
                                borderRadius: '4px',
                                backgroundColor: '#fff',
                                overflowY: 'auto',
                                flexShrink: 0
                            }}>
                                <div style={{
                                    fontFamily: 'inherit',
                                    lineHeight: '1.6',
                                    whiteSpace: 'pre-wrap'
                                }}>
                                    {(currentRecord.result_desc || '이행점검 현황 정보가 없습니다.').replace(/\\n/g, '\n')}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* 오른쪽: 미조치 사유 수정 */}
                    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                        <div style={{ height: '38px', flexShrink: 0 }}></div>
                        {currentRecord && (
                            <div style={{ padding: '8px 12px', backgroundColor: '#f5f5f5', borderRadius: '4px', marginBottom: '12px', flexShrink: 0 }}>
                                <div style={{ marginBottom: '4px', fontSize: '13px' }}>
                                    <strong>자산코드:</strong> {currentRecord.asset_cd}
                                </div>
                                <div style={{ marginBottom: '4px', fontSize: '13px' }}>
                                    <strong>진단코드:</strong> {currentRecord.measure_cd}
                                </div>
                                <div style={{ fontSize: '13px' }}>
                                    <strong>취약점:</strong> {currentRecord.measure_nm}
                                </div>
                            </div>
                        )}
                        <div style={{ height: '514px', display: 'flex', flexDirection: 'column' }}>
                            <Tabs
                                activeKey={activeTab}
                                onChange={setActiveTab}
                                style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                                items={[
                                    {
                                        key: 'edit',
                                        label: '편집',
                                        children: (
                                            <Input.TextArea
                                                value={currentRemark}
                                                onChange={(e) => setCurrentRemark(e.target.value)}
                                                placeholder="미조치 사유를 입력하세요 (마크다운 지원)"
                                                style={{
                                                    width: '100%',
                                                    height: '468px',
                                                    fontFamily: 'monospace',
                                                    resize: 'none'
                                                }}
                                            />
                                        )
                                    },
                                    {
                                        key: 'preview',
                                        label: '미리보기',
                                        children: (
                                            <div style={{
                                                height: '468px',
                                                padding: '12px',
                                                border: '1px solid #d9d9d9',
                                                borderRadius: '4px',
                                                backgroundColor: '#fff',
                                                overflowY: 'auto'
                                            }}>
                                            <ReactMarkdown
                                                remarkPlugins={[remarkGfm]}
                                                components={{
                                                    h1: ({node, ...props}) => <h1 style={{marginTop: '8px', marginBottom: '8px', fontSize: '1.8em', fontWeight: 'bold'}} {...props} />,
                                                    h2: ({node, ...props}) => <h2 style={{marginTop: '8px', marginBottom: '8px', fontSize: '1.5em', fontWeight: 'bold'}} {...props} />,
                                                    h3: ({node, ...props}) => <h3 style={{marginTop: '8px', marginBottom: '8px', fontSize: '1.3em', fontWeight: 'bold'}} {...props} />,
                                                    h4: ({node, ...props}) => <h4 style={{marginTop: '8px', marginBottom: '8px', fontSize: '1.1em', fontWeight: 'bold'}} {...props} />,
                                                    p: ({node, ...props}) => <p style={{marginBottom: '8px', lineHeight: '1.6'}} {...props} />,
                                                    ul: ({node, ...props}) => <ul style={{marginBottom: '8px', paddingLeft: '24px'}} {...props} />,
                                                    ol: ({node, ...props}) => <ol style={{marginBottom: '8px', paddingLeft: '24px'}} {...props} />,
                                                }}
                                            >
                                                {currentRemark || '*미리보기할 내용이 없습니다.*'}
                                            </ReactMarkdown>
                                        </div>
                                    )
                                }
                            ]}
                        />
                        </div>
                    </div>
                </div>
            </Modal>

            <table className='search' style={{ borderTopColor: '#dddddd', borderTopStyle: 'solid', borderTopWidth: 'thin' }}>
                <colgroup>
                    {/* <col style={{ width: '10%' }} /> */}
                    <col style={{ width: '7%' }} />
                    <col style={{ width: '10%' }} />
                    <col style={{ width: '7%' }} />
                    <col style={{ width: '10%' }} />
                    <col style={{ width: '7%' }} />
                    <col style={{ width: '10%' }} />
                    <col style={{ width: '7%' }} />
                    <col style={{ width: '10%' }} />
                    <col style={{ width: '10%' }} />
                    <col style={{ width: '10%' }} />
                </colgroup>
                <tbody>
                    <tr>
                        <td>
                            <div style={{ marginLeft: '20px' }}>총 {dataSource?.length} 개</div>
                        </td>
                        <td style={{ textAlign: 'right', width: '100%' }}>
                            <Button type="primary" onClick={() => onClickSaveHandler(changeData)}>
                                이행결과 저장
                            </Button>
                        </td>
                    </tr></tbody>
            </table>
            <Form form={form} component={false}>
                <Table
                    columns={mergedColumns}
                    dataSource={dataSource}
                    rowKey={(record) => (`new-${record.asset_cd}-${record.measure_cd}`)}
                    scroll={{ y: getGridTableHeight(400) }}
                    pagination={{ position: ['bottomCenter'], defaultPageSize: [100] }}
                    bordered size={"small"}
                    // style={{height:getGridTableHeight(260)}}
                    showSorterTooltip={false}
                    components={{
                        body: {
                            row: EditableRow,
                            cell: EditableCell,
                        },
                    }}
                    rowClassName={() => 'editable-row'}
                    // rowSelection={{ type: 'checkbox', ...rowSelection }}
                />
            </Form>
        </>

    );
}
export default PlanTableRs;